import type { TipoOpcao } from "@horofacial/shared";
import { createContext, lazy, Suspense, useCallback, useContext, useState, type ReactNode } from "react";
import { Folha } from "./Folha";
import { Campo } from "./ui";

// As listas da clínica puxam os schemas: só carregam quando um pedido de motivo as usa.
const Sugestoes = lazy(() => import("./SugestoesMotivo"));

type Pedido = {
  titulo: string;
  texto?: string;
  /** Texto do botão que confirma ("Cancelar venda", "Arquivar"). */
  acao: string;
  perigo?: boolean;
  /** Pede um motivo antes de confirmar (mínimo 0 = opcional); `sugestoes` mostra a lista da clínica para tocar. */
  motivo?: { rotulo: string; minimo?: number; sugestoes?: TipoOpcao };
  /** Caixa de marcar junto da confirmação ("Avisar a paciente pelo WhatsApp"). */
  opcao?: { rotulo: string; padrao?: boolean };
};
type Resposta = { motivo: string; opcao: boolean };
type Aberto = Pedido & { responder: (r: Resposta | null) => void };

const Contexto = createContext<(p: Pedido) => Promise<Resposta | null>>(async () => null);

/** Confirmações e pedidos de motivo na própria interface, no lugar do confirm/prompt do navegador. */
export function ProvedorConfirmacao({ children }: { children: ReactNode }) {
  const [aberto, setAberto] = useState<Aberto | null>(null);
  const pedir = useCallback(
    (p: Pedido) =>
      new Promise<Resposta | null>((resolver) =>
        setAberto({
          ...p,
          responder: (r) => {
            setAberto(null);
            resolver(r);
          },
        }),
      ),
    [],
  );
  return (
    <Contexto.Provider value={pedir}>
      {children}
      {aberto && <FolhaConfirmacao pedido={aberto} />}
    </Contexto.Provider>
  );
}

function FolhaConfirmacao({ pedido }: { pedido: Aberto }) {
  const [motivo, setMotivo] = useState("");
  const [opcao, setOpcao] = useState(pedido.opcao?.padrao ?? false);
  const [erro, setErro] = useState<string | null>(null);
  const minimo = pedido.motivo?.minimo ?? 3;
  const confirmar = () => {
    if (!pedido.motivo) return pedido.responder({ motivo: "", opcao });
    // Motivo curto não some em silêncio: a pessoa vê o que falta.
    if (motivo.trim().length < minimo) return setErro(`Escreva o motivo (pelo menos ${minimo} letras).`);
    pedido.responder({ motivo: motivo.trim(), opcao });
  };
  return (
    <Folha
      titulo={pedido.titulo}
      aoFechar={() => pedido.responder(null)}
      rodape={
        <div className="grid gap-2">
          <button type="button" className={pedido.perigo ? "botao botao-secundario w-full text-perigo" : "botao botao-primario w-full"} onClick={confirmar}>
            {pedido.acao}
          </button>
          <button type="button" className="botao botao-secundario w-full" onClick={() => pedido.responder(null)}>
            Voltar
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {pedido.texto && <p className="text-sm text-texto-suave">{pedido.texto}</p>}
        {pedido.motivo?.sugestoes && (
          <Suspense fallback={null}>
            <Sugestoes
              tipo={pedido.motivo.sugestoes}
              atual={motivo}
              aoEscolher={(v) => {
                setMotivo(v);
                setErro(null);
              }}
            />
          </Suspense>
        )}
        {pedido.motivo && (
          <Campo rotulo={pedido.motivo.rotulo} erro={erro ?? undefined}>
            <textarea
              className="campo"
              rows={3}
              autoFocus
              value={motivo}
              onChange={(e) => {
                setMotivo(e.target.value);
                setErro(null);
              }}
            />
          </Campo>
        )}
        {pedido.opcao && (
          <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm">
            <input type="checkbox" className="size-5 accent-[var(--marca)]" checked={opcao} onChange={(e) => setOpcao(e.target.checked)} />
            {pedido.opcao.rotulo}
          </label>
        )}
      </div>
    </Folha>
  );
}

/** Pedido completo (motivo e caixa de marcar juntos); null quando a pessoa desiste. */
export function usePedir() {
  return useContext(Contexto);
}

/** Pergunta antes de uma ação; true quando a pessoa confirma. */
export function useConfirmar() {
  const pedir = useContext(Contexto);
  return useCallback((p: Omit<Pedido, "motivo" | "opcao">) => pedir(p).then((r) => r !== null), [pedir]);
}

/** Como useConfirmar, com uma caixa de marcar junto; null quando a pessoa desiste. */
export function useConfirmarComOpcao() {
  const pedir = useContext(Contexto);
  return useCallback((p: Omit<Pedido, "motivo"> & { opcao: NonNullable<Pedido["opcao"]> }) => pedir(p).then((r) => (r ? { opcao: r.opcao } : null)), [pedir]);
}

/** Pede um motivo obrigatório; null quando a pessoa desiste. */
export function usePedirMotivo() {
  const pedir = useContext(Contexto);
  return useCallback(
    (p: Omit<Pedido, "motivo" | "opcao"> & { motivo: string; minimo?: number; sugestoes?: TipoOpcao }) =>
      pedir({ ...p, motivo: { rotulo: p.motivo, minimo: p.minimo, sugestoes: p.sugestoes } }).then((r) => r?.motivo ?? null),
    [pedir],
  );
}
